import React, { useCallback, useEffect, useState } from 'react';
import { get, post } from '../request';
import {
  Bot,
  CalendarClock,
  Loader2,
  Play,
  RefreshCw,
  Save,
  Sparkles,
  Package,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';

type SkillConfig = {
  enabled: boolean;
  hour: number;
  minute: number;
  daily_count: number;
  price: number;
  cookie_id: string;
  auto_create: boolean;
  channel: string;
  user_id: number;
};

const SkillDaily: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [cfg, setCfg] = useState<SkillConfig>({
    enabled: true,
    hour: 14,
    minute: 0,
    daily_count: 10,
    price: 5.9,
    cookie_id: '125024998',
    auto_create: true,
    channel: 'xianguanjia',
    user_id: 1,
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [topicPool, setTopicPool] = useState(0);
  const [lastDay, setLastDay] = useState('');
  const [last, setLast] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get<any>('/skill-daily/config');
      if (res?.config) {
        setCfg({
          enabled: !!res.config.enabled,
          hour: Number(res.config.hour ?? 14),
          minute: Number(res.config.minute ?? 0),
          daily_count: Number(res.config.daily_count ?? 10),
          price: Number(res.config.price ?? 5.9),
          cookie_id: res.config.cookie_id || '125024998',
          auto_create: res.config.auto_create !== false,
          channel: res.config.channel || 'xianguanjia',
          user_id: Number(res.config.user_id ?? 1),
        });
      }
      setPendingCount(res?.pending_count ?? 0);
      setTopicPool(res?.topic_pool_size ?? 0);
      setLastDay(res?.last_day || '');
      setLast(res?.last || null);
    } catch (e: any) {
      setMsg(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await post<any>('/skill-daily/config', cfg);
      setMsg(res?.message || '已保存');
      await load();
    } catch (e: any) {
      setMsg(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setMsg('正在归纳并发布，请稍候（约 1–3 分钟）…');
    try {
      const res = await post<any>('/skill-daily/run', {
        daily_count: cfg.daily_count,
        price: cfg.price,
        cookie_id: cfg.cookie_id,
      });
      setLast(res?.result || null);
      setMsg(res?.result?.message || (res?.success ? '执行完成' : '执行结束（部分失败）'));
      await load();
    } catch (e: any) {
      setMsg(e?.message || '执行失败');
    } finally {
      setRunning(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await post<any>('/skill-daily/preview-listing', {
        title: '短视频创作 6 件套｜OpenClaw Skill',
        intro: '选题、开头、分镜、剪辑节奏，给 Agent 装上短视频生产线。',
        price: cfg.price,
      });
      setPreview(res?.listing || null);
    } catch (e: any) {
      setMsg(e?.message || '预览失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> 加载中…
      </div>
    );
  }

  const published = last?.published || [];
  const failed = last?.failed || [];

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-gray-900">
            <Bot className="w-7 h-7 text-amber-500" />
            <h2 className="text-2xl font-extrabold tracking-tight">Skill 每日发布</h2>
          </div>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl leading-relaxed">
            每天自动<strong className="text-gray-800">归纳</strong> OpenClaw / Agent Skill 草稿，
            生成「这是 Agent 技能包、装上后能力提升」的文案，并上架闲鱼。
            默认单价 <strong className="text-amber-600">¥5.9</strong>，每天约 {cfg.daily_count} 个。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            className="h-11 px-4 rounded-xl bg-white border border-gray-200 text-sm font-semibold flex items-center gap-2 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="h-11 px-4 rounded-xl bg-gray-900 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存配置
          </button>
          <button
            onClick={runNow}
            disabled={running}
            className="h-11 px-5 rounded-xl bg-[#FFE815] text-black text-sm font-extrabold flex items-center gap-2 shadow-lg shadow-yellow-100 disabled:opacity-60"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            立即归纳+发布
          </button>
        </div>
      </div>

      {msg && (
        <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900 flex gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{msg}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: '待上架草稿', value: pendingCount, icon: Package },
          { label: '主题池可创建', value: topicPool, icon: Sparkles },
          { label: '上次执行日', value: lastDay || '—', icon: CalendarClock },
          { label: '定时状态', value: cfg.enabled ? '已开启' : '关闭', icon: Bot },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 text-gray-400 text-xs font-semibold mb-2">
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </div>
            <div className="text-xl font-extrabold text-gray-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-5">
          <h3 className="font-extrabold text-gray-900 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-amber-500" /> 定时配置
          </h3>

          <label className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-gray-50">
            <span className="text-sm font-semibold text-gray-800">启用每日自动发布</span>
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="w-5 h-5"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="text-gray-500 font-medium">每天时刻 · 时</span>
              <input
                type="number"
                min={0}
                max={23}
                value={cfg.hour}
                onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
                className="mt-1 w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-100 font-semibold"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-500 font-medium">分</span>
              <input
                type="number"
                min={0}
                max={59}
                value={cfg.minute}
                onChange={(e) => setCfg({ ...cfg, minute: Number(e.target.value) })}
                className="mt-1 w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-100 font-semibold"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-500 font-medium">每日数量</span>
              <input
                type="number"
                min={1}
                max={20}
                value={cfg.daily_count}
                onChange={(e) => setCfg({ ...cfg, daily_count: Number(e.target.value) })}
                className="mt-1 w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-100 font-semibold"
              />
            </label>
            <label className="text-sm">
              <span className="text-gray-500 font-medium">单价（元）</span>
              <input
                type="number"
                min={0.1}
                step={0.1}
                value={cfg.price}
                onChange={(e) => setCfg({ ...cfg, price: Number(e.target.value) })}
                className="mt-1 w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-100 font-semibold"
              />
            </label>
          </div>

          <label className="text-sm block">
            <span className="text-gray-500 font-medium">发货账号 cookie_id</span>
            <input
              value={cfg.cookie_id}
              onChange={(e) => setCfg({ ...cfg, cookie_id: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-xl bg-gray-50 border border-gray-100 font-semibold"
              placeholder="125024998"
            />
          </label>

          <label className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-gray-50">
            <div>
              <div className="text-sm font-semibold text-gray-800">草稿不足时自动创建</div>
              <div className="text-xs text-gray-500 mt-0.5">从 OpenClaw Skill 主题池补齐今日数量</div>
            </div>
            <input
              type="checkbox"
              checked={cfg.auto_create}
              onChange={(e) => setCfg({ ...cfg, auto_create: e.target.checked })}
              className="w-5 h-5"
            />
          </label>

          <div className="text-xs text-gray-500 leading-relaxed bg-blue-50/80 rounded-2xl p-3 border border-blue-100">
            <strong className="text-blue-800">文案策略：</strong>
            标题与描述会强调「这是 OpenClaw / AI Agent 的 Skill 技能包」，
            写清装上后 Agent 多什么能力、提升在哪里；默认 ¥{cfg.price}，库存 1（未开鱼小铺）。
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-extrabold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" /> 文案预览（样例）
            </h3>
            <button
              onClick={loadPreview}
              className="text-xs font-bold px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
            >
              生成预览
            </button>
          </div>
          {preview ? (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-gray-400 font-semibold mb-1">标题</div>
                <div className="font-extrabold text-gray-900">{preview.listing_title}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-semibold mb-1">价格</div>
                <div className="font-extrabold text-amber-600 text-lg">¥{preview.suggest_price}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-semibold mb-1">描述</div>
                <pre className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700 bg-gray-50 rounded-2xl p-4 max-h-80 overflow-auto">
                  {preview.listing_description}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-12 text-center">点击「生成预览」查看 Agent Skill 风格文案</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
        <h3 className="font-extrabold text-gray-900 mb-4">最近一次执行</h3>
        {!last || (!last.message && !published.length && !failed.length) ? (
          <p className="text-sm text-gray-400">暂无执行记录</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 font-medium">{last.message}</p>
            {published.length > 0 && (
              <div>
                <div className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> 成功 {published.length}
                </div>
                <div className="space-y-2">
                  {published.map((p: any, i: number) => (
                    <div key={i} className="text-xs bg-green-50 rounded-xl px-3 py-2 text-green-900">
                      #{p.product_id} {p.listing_title || p.title} · item {p.xg?.item_id || '—'} · ¥{p.price}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {failed.length > 0 && (
              <div>
                <div className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> 失败 {failed.length}
                </div>
                <div className="space-y-2">
                  {failed.map((p: any, i: number) => (
                    <div key={i} className="text-xs bg-red-50 rounded-xl px-3 py-2 text-red-900">
                      #{p.product_id} {p.title} · {p.xg?.error || p.error || p.xg?.message || 'failed'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillDaily;
